import type { SxProps, Theme } from "@mui/material/styles";

export const templateStatusChipSx: SxProps<Theme> = {
  height: 20,
  maxWidth: "100%",
  "& .MuiChip-label": {
    px: 0.75,
    py: 0,
    fontSize: "0.8rem",
  },
};

export const padraoFluxmedChipSx: SxProps<Theme> = {
  ...templateStatusChipSx,
  color: "text.secondary",
  borderColor: "grey.400",
  bgcolor: "action.hover",
};

function normalizeOrganizationPrefix(value?: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export type FluxmedWhatsappChannelOption = "fluxmedDefault" | "metaEmbeddedSignup";

export function resolveDefaultTemplatesHasActiveMetaFlag(params: {
  whatsappChannelOption?: FluxmedWhatsappChannelOption | null;
  hasActiveMetaEndpoint?: boolean;
  isConnectionStatusLoading?: boolean;
}): boolean | undefined {
  if (params.isConnectionStatusLoading) {
    return undefined;
  }

  if (params.whatsappChannelOption === "fluxmedDefault") {
    return false;
  }
  if (params.whatsappChannelOption === "metaEmbeddedSignup") {
    return true;
  }

  return params.hasActiveMetaEndpoint ? true : false;
}

export const FLUXMED_PLATFORM_DEFAULT_TEMPLATE_KEYS = [
  "fluxmedagendamentoteleconsulta",
  "fluxmedcancelamentoteleconsulta",
  "fluxmed_agendamento_consulta",
  "fluxmed_cancelamento_consulta",
  "teste_flow_medication",
  "fluxmedpagamentoconsulta",
  "flow_service_request_status",
  "fluxmed_crm_v1",
] as const;

export type FluxmedPlatformDefaultTemplateKey =
  (typeof FLUXMED_PLATFORM_DEFAULT_TEMPLATE_KEYS)[number];

const FLUXMED_PLATFORM_DEFAULT_TEMPLATE_KEYS_SORTED = [
  ...FLUXMED_PLATFORM_DEFAULT_TEMPLATE_KEYS,
].sort((left, right) => right.length - left.length);

function normalizeTemplateSlugKey(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\./g, "_");
}

function stripTemplateLanguageSuffix(normalized: string): string {
  return normalized.replace(/_(pt_br|en_us|es_\w{2,3})$/i, "");
}

function matchesFluxmedPlatformDefaultKey(
  normalizedName: string,
  normalizedKey: string,
): boolean {
  const candidates = [
    normalizedName,
    stripTemplateLanguageSuffix(normalizedName),
  ];

  for (const candidate of candidates) {
    if (candidate === normalizedKey || candidate.endsWith(`_${normalizedKey}`)) {
      return true;
    }

    const index = candidate.indexOf(normalizedKey);
    if (index < 0) continue;

    const charBefore = index === 0 ? "_" : candidate.charAt(index - 1);
    const charAfter = candidate.charAt(index + normalizedKey.length);
    const hasValidBoundaryBefore = charBefore === "_";
    const hasValidBoundaryAfter =
      charAfter === "" || charAfter === "_" || charAfter === undefined;

    if (hasValidBoundaryBefore && hasValidBoundaryAfter) {
      return true;
    }
  }

  return false;
}

export function resolveFluxmedPlatformDefaultTemplateKey(
  templateName: string,
): FluxmedPlatformDefaultTemplateKey | null {
  const normalized = normalizeTemplateSlugKey(templateName);

  for (const key of FLUXMED_PLATFORM_DEFAULT_TEMPLATE_KEYS_SORTED) {
    const normalizedKey = normalizeTemplateSlugKey(key);
    if (matchesFluxmedPlatformDefaultKey(normalized, normalizedKey)) {
      return key;
    }
  }

  return null;
}

export function isFluxmedPlatformDefaultMessageTemplate(
  templateName: string,
): boolean {
  return resolveFluxmedPlatformDefaultTemplateKey(templateName) != null;
}

export function filterPlatformDefaultMessageTemplates<T extends { name: string }>(
  templates: readonly T[] | null | undefined,
  organizationPrefix?: string,
): T[] {
  const safeTemplates = Array.isArray(templates) ? templates : [];
  const prefix = String(organizationPrefix ?? "").trim();

  return safeTemplates.filter((template) => {
    if (!isFluxmedPlatformDefaultMessageTemplate(template.name)) {
      return false;
    }
    if (!prefix) return true;
    return !hasTemplateOrganizationPrefix({
      templateName: template.name,
      organizationPrefix: prefix,
    });
  });
}

export function filterMetaDefaultMessageTemplates<T extends { name: string }>(
  templates: readonly T[] | null | undefined,
  organizationPrefix?: string,
): T[] {
  const safeTemplates = Array.isArray(templates) ? templates : [];
  const prefix = String(organizationPrefix ?? "").trim();
  if (!prefix) return [];

  return safeTemplates.filter((template) => {
    if (
      !hasTemplateOrganizationPrefix({
        templateName: template.name,
        organizationPrefix: prefix,
      })
    ) {
      return false;
    }
    return (
      resolveFluxmedMetaDefaultTemplateSlugKey(
        template.name,
        organizationPrefix,
      ) != null
    );
  });
}

export function filterMessageTemplatesByOrganization<T extends { name: string }>(
  templates: readonly T[] | null | undefined,
  organizationId?: string,
): T[] {
  const safeTemplates = Array.isArray(templates) ? templates : [];
  const prefix = String(organizationId ?? "").trim();
  if (!prefix) return [];

  return safeTemplates.filter((template) =>
    hasTemplateOrganizationPrefix({
      templateName: template.name,
      organizationPrefix: prefix,
    }),
  );
}

export function isAnyFluxmedDefaultMessageTemplate(
  templateName: string,
  organizationPrefix?: string,
): boolean {
  return (
    resolveFluxmedMetaDefaultTemplateSlugKey(
      templateName,
      organizationPrefix,
    ) != null || resolveFluxmedPlatformDefaultTemplateKey(templateName) != null
  );
}

export function hasTemplateOrganizationPrefix(params: {
  templateName: string;
  organizationPrefix?: string;
}): boolean {
  const normalizedPrefix = normalizeOrganizationPrefix(params.organizationPrefix);
  if (!normalizedPrefix) return false;

  const normalizedName = String(params.templateName ?? "")
    .trim()
    .toLowerCase();

  if (normalizedName === normalizedPrefix) return true;

  return (
    normalizedName.startsWith(`${normalizedPrefix}_`) ||
    normalizedName.startsWith(`${normalizedPrefix}-`)
  );
}

export function stripOrganizationPrefixFromTemplateName(params: {
  templateName: string;
  organizationPrefix?: string;
}): string {
  const normalizedPrefix = normalizeOrganizationPrefix(params.organizationPrefix);
  const rawName = String(params.templateName ?? "").trim();
  if (!normalizedPrefix) return rawName;

  const lowerName = rawName.toLowerCase();
  if (lowerName === normalizedPrefix) return "";

  const withUnderscore = `${normalizedPrefix}_`;
  if (lowerName.startsWith(withUnderscore)) {
    return rawName.slice(withUnderscore.length).trim();
  }

  const withHyphen = `${normalizedPrefix}-`;
  if (lowerName.startsWith(withHyphen)) {
    return rawName.slice(withHyphen.length).trim();
  }

  return rawName;
}

function capitalizeTemplateWord(word: string): string {
  const trimmed = String(word ?? "").trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function titleCaseTemplateSlug(slug: string): string {
  return String(slug ?? "")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(capitalizeTemplateWord)
    .join(" ");
}

export const FLUXMED_META_DEFAULT_TEMPLATE_SLUGS = [
  "flow_lembrete",
  "flow_medication",
  "flow_exame",
  "cobranca_de_consulta",
  "cancelamento_de_consulta",
  "agendamento_de_consulta",
] as const;

export const FLUXMED_DEFAULT_TEMPLATE_SLUGS = FLUXMED_META_DEFAULT_TEMPLATE_SLUGS;

export type FluxmedMetaDefaultTemplateSlug =
  (typeof FLUXMED_META_DEFAULT_TEMPLATE_SLUGS)[number];

export type FluxmedDefaultTemplateCategory =
  | "cancelamento"
  | "agendamento"
  | "pagamento"
  | "outros";

const FLUXMED_META_DEFAULT_TEMPLATE_SLUG_SET = new Set<string>(
  FLUXMED_META_DEFAULT_TEMPLATE_SLUGS,
);

const FLUXMED_META_DEFAULT_TEMPLATE_DISPLAY_NAMES: Record<
  FluxmedMetaDefaultTemplateSlug,
  string
> = {
  flow_lembrete: "Lembrete",
  flow_medication: "Medicação",
  flow_exame: "Exame",
  cobranca_de_consulta: "Cobrança de consulta",
  cancelamento_de_consulta: "Cancelamento de consulta",
  agendamento_de_consulta: "Agendamento de consulta",
};

const FLUXMED_PLATFORM_DEFAULT_TEMPLATE_DISPLAY_NAMES: Record<
  FluxmedPlatformDefaultTemplateKey,
  string
> = {
  fluxmedagendamentoteleconsulta: "Agendamento (Teleconsulta)",
  fluxmedcancelamentoteleconsulta: "Cancelamento (Teleconsulta)",
  fluxmed_agendamento_consulta: "Agendamento de consulta",
  fluxmed_cancelamento_consulta: "Cancelamento de consulta",
  teste_flow_medication: "Medicação",
  fluxmedpagamentoconsulta: "Pagamento de consulta",
  flow_service_request_status: "Status de solicitação",
  fluxmed_crm_v1: "Lembrete",
};

const FLUXMED_META_DEFAULT_SLUG_CATEGORY: Record<
  FluxmedMetaDefaultTemplateSlug,
  FluxmedDefaultTemplateCategory
> = {
  agendamento_de_consulta: "agendamento",
  cancelamento_de_consulta: "cancelamento",
  cobranca_de_consulta: "pagamento",
  flow_lembrete: "agendamento",
  flow_medication: "outros",
  flow_exame: "outros",
};

const FLUXMED_PLATFORM_DEFAULT_KEY_CATEGORY: Record<
  FluxmedPlatformDefaultTemplateKey,
  FluxmedDefaultTemplateCategory
> = {
  fluxmedagendamentoteleconsulta: "agendamento",
  fluxmedcancelamentoteleconsulta: "cancelamento",
  fluxmed_agendamento_consulta: "agendamento",
  fluxmed_cancelamento_consulta: "cancelamento",
  teste_flow_medication: "outros",
  fluxmedpagamentoconsulta: "pagamento",
  flow_service_request_status: "outros",
  fluxmed_crm_v1: "outros",
};

export interface FluxmedDefaultTemplateMatchOptions {
  organizationPrefix?: string;
  hasActiveMetaEndpoint?: boolean;
}

export function resolveFluxmedMetaDefaultTemplateSlugKey(
  templateName: string,
  organizationPrefix?: string,
): FluxmedMetaDefaultTemplateSlug | null {
  const normalizedFull = normalizeTemplateSlugKey(templateName);
  const stripped = normalizeTemplateSlugKey(
    stripOrganizationPrefixFromTemplateName({
      templateName,
      organizationPrefix,
    }),
  );

  const candidates =
    stripped && stripped !== normalizedFull
      ? [stripped, normalizedFull]
      : [normalizedFull];

  for (const candidate of candidates) {
    if (FLUXMED_META_DEFAULT_TEMPLATE_SLUG_SET.has(candidate)) {
      return candidate as FluxmedMetaDefaultTemplateSlug;
    }
  }

  for (const candidate of candidates) {
    for (const slug of FLUXMED_META_DEFAULT_TEMPLATE_SLUGS) {
      if (candidate === slug || candidate.endsWith(`_${slug}`)) {
        return slug;
      }
    }
  }

  return null;
}

export const resolveFluxmedDefaultTemplateSlugKey =
  resolveFluxmedMetaDefaultTemplateSlugKey;

export function formatFluxmedDefaultTemplateDisplayName(
  templateName: string,
  organizationPrefix?: string,
  hasActiveMetaEndpoint?: boolean,
): string {
  const metaSlug = resolveFluxmedMetaDefaultTemplateSlugKey(
    templateName,
    organizationPrefix,
  );
  if (
    metaSlug &&
    (hasActiveMetaEndpoint === true || hasActiveMetaEndpoint === undefined)
  ) {
    return FLUXMED_META_DEFAULT_TEMPLATE_DISPLAY_NAMES[metaSlug];
  }

  const platformKey = resolveFluxmedPlatformDefaultTemplateKey(templateName);
  if (
    platformKey &&
    (hasActiveMetaEndpoint === false || hasActiveMetaEndpoint === undefined)
  ) {
    return FLUXMED_PLATFORM_DEFAULT_TEMPLATE_DISPLAY_NAMES[platformKey];
  }

  const category = getFluxmedDefaultTemplateCategory(
    templateName,
    organizationPrefix,
    hasActiveMetaEndpoint,
  );
  const fallbackSlug = normalizeTemplateSlugKey(
    stripOrganizationPrefixFromTemplateName({
      templateName,
      organizationPrefix,
    }),
  );
  const descriptiveTitle = titleCaseTemplateSlug(fallbackSlug);
  if (descriptiveTitle && category !== "outros") {
    return `${getFluxmedDefaultCategoryLabel(category)} — ${descriptiveTitle}`;
  }
  return descriptiveTitle || "Template padrão";
}

export function formatTemplateDisplayName(
  name: string,
  params?: {
    organizationPrefix?: string;
    hasActiveMetaEndpoint?: boolean;
  },
): string {
  if (
    isFluxmedDefaultMessageTemplate(name, params?.organizationPrefix, {
      hasActiveMetaEndpoint: params?.hasActiveMetaEndpoint,
    })
  ) {
    return formatFluxmedDefaultTemplateDisplayName(
      name,
      params?.organizationPrefix,
      params?.hasActiveMetaEndpoint,
    );
  }

  const rawDisplayName = stripOrganizationPrefixFromTemplateName({
    templateName: name,
    organizationPrefix: params?.organizationPrefix,
  });

  const normalized = String(rawDisplayName || name || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return normalized;
}

export function isMessageTemplateApproved(status?: string): boolean {
  return String(status ?? "").trim().toUpperCase() === "APPROVED";
}

export function isMessageTemplateListedForSend(status?: string): boolean {
  const normalized = String(status ?? "").trim();
  if (!normalized) return true;
  return isMessageTemplateApproved(status);
}

export function getStatusUi(status?: string): {
  label: string;
  color: "success" | "warning" | "error" | "default";
} {
  const normalized = String(status ?? "").trim().toUpperCase();
  if (normalized === "APPROVED") return { label: "Aprovado", color: "success" };
  if (normalized === "PENDING") return { label: "Pendente", color: "warning" };
  if (normalized === "REJECTED") return { label: "Rejeitado", color: "error" };
  if (normalized === "PAUSED") return { label: "Pausado", color: "default" };
  return { label: normalized || "Sem status", color: "default" };
}

export function buildTemplatePreviewPlain(template: {
  header?: string;
  preview?: string;
}): string {
  const header = String(template.header ?? "").trim();
  const body = String(template.preview ?? "").trim();
  if (header && body) return `${header}\n${body}`;
  return header || body || "";
}

export type MessageTemplateListScope = "organization" | "default";

export function isFluxmedDefaultMessageTemplate(
  templateName: string,
  organizationPrefix?: string,
  options?: FluxmedDefaultTemplateMatchOptions,
): boolean {
  const hasActiveMetaEndpoint = options?.hasActiveMetaEndpoint;

  if (hasActiveMetaEndpoint === true) {
    return (
      resolveFluxmedMetaDefaultTemplateSlugKey(
        templateName,
        organizationPrefix,
      ) != null
    );
  }

  if (hasActiveMetaEndpoint === false) {
    return isFluxmedPlatformDefaultMessageTemplate(templateName);
  }

  return isAnyFluxmedDefaultMessageTemplate(templateName, organizationPrefix);
}

export function getFluxmedDefaultTemplateCategory(
  templateName: string,
  organizationPrefix?: string,
  hasActiveMetaEndpoint?: boolean,
): FluxmedDefaultTemplateCategory {
  const metaSlug = resolveFluxmedMetaDefaultTemplateSlugKey(
    templateName,
    organizationPrefix,
  );
  if (
    metaSlug &&
    (hasActiveMetaEndpoint === true || hasActiveMetaEndpoint === undefined)
  ) {
    return FLUXMED_META_DEFAULT_SLUG_CATEGORY[metaSlug];
  }

  if (hasActiveMetaEndpoint === false) {
    const platformKey = resolveFluxmedPlatformDefaultTemplateKey(templateName);
    if (platformKey) {
      return FLUXMED_PLATFORM_DEFAULT_KEY_CATEGORY[platformKey];
    }
    return "outros";
  }

  const platformKey = resolveFluxmedPlatformDefaultTemplateKey(templateName);
  if (platformKey) {
    return FLUXMED_PLATFORM_DEFAULT_KEY_CATEGORY[platformKey];
  }

  const slug = normalizeTemplateSlugKey(
    stripOrganizationPrefixFromTemplateName({
      templateName,
      organizationPrefix,
    }),
  );

  if (slug.includes("cancelamento")) return "cancelamento";
  if (slug.includes("agendamento")) return "agendamento";
  if (slug.includes("pagamento") || slug.includes("cobranca")) return "pagamento";
  return "outros";
}

export function filterMessageTemplatesByListScope<T extends { name: string }>(
  templates: readonly T[] | null | undefined,
  scope: MessageTemplateListScope,
  organizationPrefix?: string,
  hasActiveMetaEndpoint?: boolean,
): T[] {
  const safeTemplates = Array.isArray(templates) ? templates : [];
  return safeTemplates.filter((template) => {
    const isDefault = isFluxmedDefaultMessageTemplate(
      template.name,
      organizationPrefix,
      { hasActiveMetaEndpoint },
    );
    return scope === "default" ? isDefault : !isDefault;
  });
}

export function filterMessageTemplatesForDefaultTabView<T extends { name: string }>(
  templates: readonly T[] | null | undefined,
  organizationPrefix?: string,
  hasActiveMetaEndpoint?: boolean,
): T[] {
  const safeTemplates = Array.isArray(templates) ? templates : [];
  if (hasActiveMetaEndpoint === undefined) return [];

  if (hasActiveMetaEndpoint === false) {
    return filterPlatformDefaultMessageTemplates(
      safeTemplates,
      organizationPrefix,
    );
  }

  return filterMetaDefaultMessageTemplates(safeTemplates, organizationPrefix);
}

/** Meus modelos: somente templates da org (com prefixo) que não são padrão Fluxmed. */
export function filterMessageTemplatesForOrganizationTabView<T extends {
  name: string;
}>(
  templates: readonly T[] | null | undefined,
  organizationPrefix?: string,
  _hasActiveMetaEndpoint?: boolean,
): T[] {
  const safeTemplates = Array.isArray(templates) ? templates : [];
  const prefix = String(organizationPrefix ?? "").trim();
  if (!prefix) return [];

  return safeTemplates.filter((template) => {
    if (isAnyFluxmedDefaultMessageTemplate(template.name, organizationPrefix)) {
      return false;
    }

    return hasTemplateOrganizationPrefix({
      templateName: template.name,
      organizationPrefix: prefix,
    });
  });
}

export function filterFluxmedDefaultTemplatesByCategory<T extends { name: string }>(
  templates: readonly T[],
  category: FluxmedDefaultTemplateCategory | "all",
  organizationPrefix?: string,
  hasActiveMetaEndpoint?: boolean,
): T[] {
  if (category === "all") return [...templates];
  return templates.filter(
    (template) =>
      getFluxmedDefaultTemplateCategory(
        template.name,
        organizationPrefix,
        hasActiveMetaEndpoint,
      ) === category,
  );
}

export function getFluxmedDefaultCategoryLabel(
  category: FluxmedDefaultTemplateCategory,
): string {
  if (category === "cancelamento") return "Cancelamento";
  if (category === "agendamento") return "Agendamento";
  if (category === "pagamento") return "Pagamento";
  return "Outros";
}
